import { cssProps } from '@oracle/oraclejet-cssprops';
import { useCallback, useEffect, useState } from 'preact/hooks';

const offScreenStyle = cssProps`${{
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflowX: 'hidden',
  overflowY: 'hidden',
  paddingTop: 0,
  paddingRight: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  width: '1px',
  position: 'absolute'
}}`;

type Props = {
  /**
   * Whether this live region is atomic
   */
  atomic?: 'true' | 'false';

  /**
   * The text to be read out in the live region
   */
  children?: string;

  /**
   * Timeout for updating the text
   */
  timeout?: number;

  /**
   * The value for the aria-live attribute
   */
  type?: 'assertive' | 'polite' | 'off';
};

/**
 * A helper component that renders an aria-live region
 *
 * TODO: Create a more centralized component that can handle aria-live region for
 * the whole application and use context api to communicate
 */
function LiveRegion({ atomic = 'false', children = '', timeout = 100, type = 'polite' }: Props) {
  const ariaLiveText = useLiveText(children, timeout);

  return (
    <span aria-live={type} aria-atomic={atomic} class={offScreenStyle}>
      {ariaLiveText}
    </span>
  );
}

/**
 * A custom hook for handling the aria-live region
 *
 * @param text The aria-live text to use
 * @param timeout The timeout for setting the aria-live text async
 * @returns The aria-live text
 */
function useLiveText(text: string, timeout: number) {
  const [liveText, setLiveText] = useState('');
  const updateText = useCallback(() => setLiveText(text), [text]);
  const updateTextAsync = useCallback(() => setTimeout(updateText, timeout), [updateText, timeout]);

  useEffect(() => {
    const timeoutId = updateTextAsync();

    return () => clearTimeout(timeoutId);
  }, [updateTextAsync]);

  return liveText;
}

export { LiveRegion };
